import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button, Flex, Icon, Text } from '@chakra-ui/react';
import type { SlashMenuCommandType } from '../../constants/slashMenuCommands';

export interface SuggestionsMenuRef {
  onKeyDown: (opts: { event: KeyboardEvent }) => boolean;
}

export type SuggestionsMenuItem = SlashMenuCommandType & { highlightedName?: string };

interface CommandListProps {
  items: SuggestionsMenuItem[];
  command: (item: SuggestionsMenuItem) => void;
}

export const SuggestionsMenu = React.forwardRef(function SuggestionsMenu(
  { items, command }: CommandListProps,
  ref,
) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  const scrollToItem = useCallback((index: number) => {
    itemRefs.current[index]?.scrollIntoView({ block: 'nearest' });
  }, []);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();
        const next = (selectedIndex + items.length - 1) % items.length;
        setSelectedIndex(next);
        scrollToItem(next);
        return true;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        event.stopPropagation();
        const next = (selectedIndex + 1) % items.length;
        setSelectedIndex(next);
        scrollToItem(next);
        return true;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        selectItem(selectedIndex);
        return true;
      }

      return false;
    },
  }));

  const selectItem = (index: number) => {
    const item = items[index];

    if (item) {
      setTimeout(() => command(item));
    }
  };

  return (
    <Flex
      direction="column"
      zIndex={1100}
      bg="bg.surface"
      shadow="sm"
      gap={2}
      maxH="300px"
      minW="250px"
      overflowY="scroll"
      overflowX="hidden"
      py={2}
      px={0}
      borderRadius="md"
      borderWidth="1px"
      borderColor="border.default"
      outline="2px solid transparent"
      outlineOffset="2px"
    >
      {items.length ? (
        <>
          {items.map((item, index) => {
            return (
              <Button
                w="full"
                variant="tertiary"
                justifyContent="start"
                gap={2}
                bg={selectedIndex === index ? 'bg.muted' : 'transparent'}
                borderRadius={0}
                key={item.name}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                onClick={() => selectItem(index)}
                onMouseEnter={() => setSelectedIndex(index)}
                onKeyDown={(e) => {
                  if (e.code === 'Enter') {
                    selectItem(index);
                  }
                }}
                leftIcon={<Icon as={item.icon} boxSize="24px" />}
                fontWeight="normal"
                textAlign="left"
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: item.highlightedName || item.name,
                  }}
                />
              </Button>
            );
          })}
        </>
      ) : (
        <Text p={2} color="fg.muted">
          No result
        </Text>
      )}
    </Flex>
  );
});
